import { calculateBurndownSnapshot } from "./burndown.utils";
import type { ChartData as BurndownChartData } from "../../types/burndown.types";
import type {
  FinancialDataCache,
  MonthlyFinancialData,
} from "./financial-cache.utils";
import { getDateRangeData } from "./financial-cache.utils";
import {
  startOfMonthUTC,
  sumLiquidAssetBookValue,
} from "@burnchart/financial";

export type ChartGranularity = "biweekly" | "monthly" | "quarterly";

export interface QuarterlyPeriodData {
  quarterKey: string;
  label: string;
  periodStart: Date;
  periodEnd: Date;
  revenue: number;
  expenses: number;
  netOperationalFlow: number;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const BIWEEK_DAYS = 14;

export interface ChartPeriodData extends MonthlyFinancialData {
  granularity: ChartGranularity;
  periodStart: Date;
  periodEnd: Date;
}

/** Ranges under one calendar year use bi-weekly buckets for charts and timeline ticks. */
export function getChartGranularity(
  startDate: Date,
  endDate: Date,
): ChartGranularity {
  const oneYearMs = 365.25 * MS_PER_DAY;
  return endDate.getTime() - startDate.getTime() < oneYearMs
    ? "biweekly"
    : "monthly";
}

function endOfMonthUTC(month: Date): Date {
  const end = new Date(month);
  end.setUTCMonth(end.getUTCMonth() + 1);
  end.setUTCDate(0);
  end.setUTCHours(23, 59, 59, 999);
  return end;
}

function daysInclusive(start: Date, end: Date): number {
  if (end.getTime() < start.getTime()) return 0;
  return (
    Math.floor((end.getTime() - start.getTime()) / MS_PER_DAY) + 1
  );
}

function daysInMonthUTC(month: Date): number {
  const start = startOfMonthUTC(month);
  const end = endOfMonthUTC(start);
  return daysInclusive(start, end);
}

function monthKeyFor(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** Prorate monthly cache flows across the days overlapping a period. */
function prorateFlowsForPeriod(
  cache: FinancialDataCache,
  periodStart: Date,
  periodEnd: Date,
): Pick<
  MonthlyFinancialData,
  | "revenue"
  | "expenses"
  | "oneTimeExpenses"
  | "funding"
  | "assetAcquisitions"
  | "assetImpact"
  | "netCashFlow"
> {
  let revenue = 0;
  let expenses = 0;
  let oneTimeExpenses = 0;
  let funding = 0;
  let assetAcquisitions = 0;
  let assetImpact = 0;
  let netCashFlow = 0;

  let cursor = startOfMonthUTC(periodStart);
  const lastMonth = startOfMonthUTC(periodEnd);

  while (cursor.getTime() <= lastMonth.getTime()) {
    const monthData = cache.monthlyData.get(monthKeyFor(cursor));
    if (monthData) {
      const monthStart = startOfMonthUTC(cursor);
      const monthEnd = endOfMonthUTC(cursor);
      const overlapStart = new Date(
        Math.max(periodStart.getTime(), monthStart.getTime()),
      );
      const overlapEnd = new Date(
        Math.min(periodEnd.getTime(), monthEnd.getTime()),
      );
      const overlapDays = daysInclusive(overlapStart, overlapEnd);
      const dim = daysInMonthUTC(cursor);
      if (overlapDays > 0 && dim > 0) {
        const fraction = overlapDays / dim;
        revenue += monthData.revenue * fraction;
        expenses += monthData.expenses * fraction;
        oneTimeExpenses += monthData.oneTimeExpenses * fraction;
        funding += monthData.funding * fraction;
        assetAcquisitions += monthData.assetAcquisitions * fraction;
        assetImpact += monthData.assetImpact * fraction;
        netCashFlow += monthData.netCashFlow * fraction;
      }
    }
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }

  return {
    revenue,
    expenses,
    oneTimeExpenses,
    funding,
    assetAcquisitions,
    assetImpact,
    netCashFlow,
  };
}

function buildBiweeklyPeriods(
  cache: FinancialDataCache,
  chartData: BurndownChartData,
  startDate: Date,
  endDate: Date,
): ChartPeriodData[] {
  const periods: ChartPeriodData[] = [];
  let periodStart = new Date(startDate);

  while (periodStart.getTime() <= endDate.getTime()) {
    const periodEnd = new Date(periodStart);
    periodEnd.setUTCDate(periodEnd.getUTCDate() + BIWEEK_DAYS - 1);
    periodEnd.setUTCHours(23, 59, 59, 999);

    if (periodEnd.getTime() > endDate.getTime()) {
      periodEnd.setTime(endDate.getTime());
    }

    const flows = prorateFlowsForPeriod(cache, periodStart, periodEnd);
    const snapshot = calculateBurndownSnapshot(chartData, periodEnd);
    const monthAtEnd = cache.monthlyData.get(monthKeyFor(periodEnd));
    const liquidAssetValue = sumLiquidAssetBookValue(
      chartData.events,
      periodEnd,
    );

    periods.push({
      month: new Date(periodEnd),
      monthKey: `bw-${periodEnd.toISOString().slice(0, 10)}`,
      ...flows,
      liquidAssetValue,
      cumulativeCash: snapshot.cashBalance,
      customerMetrics: monthAtEnd?.customerMetrics,
      granularity: "biweekly",
      periodStart: new Date(periodStart),
      periodEnd: new Date(periodEnd),
    });

    periodStart = new Date(periodEnd);
    periodStart.setUTCDate(periodStart.getUTCDate() + 1);
    periodStart.setUTCHours(0, 0, 0, 0);
  }

  return periods;
}

function startOfQuarterUTC(date: Date): Date {
  const quarterStartMonth = Math.floor(date.getUTCMonth() / 3) * 3;
  return startOfMonthUTC(
    new Date(Date.UTC(date.getUTCFullYear(), quarterStartMonth, 1)),
  );
}

function endOfQuarterUTC(quarterStart: Date): Date {
  const end = new Date(quarterStart);
  end.setUTCMonth(end.getUTCMonth() + 3);
  end.setUTCDate(0);
  end.setUTCHours(23, 59, 59, 999);
  return end;
}

function quarterKeyFor(date: Date): string {
  const quarter = Math.floor(date.getUTCMonth() / 3) + 1;
  return `${date.getUTCFullYear()}-Q${quarter}`;
}

export function formatQuarterLabel(periodStart: Date): string {
  const quarter = Math.floor(periodStart.getUTCMonth() / 3) + 1;
  const year = String(periodStart.getUTCFullYear()).slice(-2);
  return `Q${quarter} '${year}`;
}

export function getQuarterlyPeriodData(
  cache: FinancialDataCache,
  startDate: Date,
  endDate: Date,
): QuarterlyPeriodData[] {
  const periods: QuarterlyPeriodData[] = [];
  let periodStart = startOfQuarterUTC(startDate);

  while (periodStart.getTime() <= endDate.getTime()) {
    const periodEnd = endOfQuarterUTC(periodStart);
    const effectiveStart = new Date(
      Math.max(periodStart.getTime(), startDate.getTime()),
    );
    // Always aggregate through the full calendar quarter so the last bar is not
    // prorated to the visible range end (quarterly charts are quarter buckets).
    const effectiveEnd = periodEnd;

    if (effectiveStart.getTime() <= effectiveEnd.getTime()) {
      const flows = prorateFlowsForPeriod(cache, effectiveStart, effectiveEnd);
      const revenue = Math.round(flows.revenue + flows.assetImpact);
      const expenses = Math.round(flows.expenses + flows.oneTimeExpenses);

      periods.push({
        quarterKey: quarterKeyFor(periodStart),
        label: formatQuarterLabel(periodStart),
        periodStart: new Date(periodStart),
        periodEnd: new Date(periodEnd),
        revenue,
        expenses,
        netOperationalFlow: revenue - expenses,
      });
    }

    periodStart = new Date(periodStart);
    periodStart.setUTCMonth(periodStart.getUTCMonth() + 3);
  }

  return periods;
}

export function getChartPeriodData(
  cache: FinancialDataCache,
  chartData: BurndownChartData,
  startDate: Date,
  endDate: Date,
): ChartPeriodData[] {
  const granularity = getChartGranularity(startDate, endDate);

  if (granularity === "monthly") {
    return getDateRangeData(cache, startDate, endDate).map((monthData) => ({
      ...monthData,
      granularity: "monthly" as const,
      periodStart: startOfMonthUTC(monthData.month),
      periodEnd: endOfMonthUTC(monthData.month),
    }));
  }

  return buildBiweeklyPeriods(cache, chartData, startDate, endDate);
}

export function formatChartPeriodLabel(
  period: ChartPeriodData,
  style: "short" | "long" = "short",
): string {
  const date = period.periodEnd;
  if (period.granularity === "biweekly") {
    if (style === "long") {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }

  if (style === "long") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
  });
}

export function getChartPeriodCountLabel(
  count: number,
  granularity: ChartGranularity,
): string {
  if (granularity === "biweekly") {
    return `${count} bi-weekly periods`;
  }
  return `${count} months`;
}

export function formatCompactCurrency(value: number): string {
  const absoluteValue = Math.abs(Math.round(value));
  if (absoluteValue >= 1_000_000) {
    return `$${(absoluteValue / 1_000_000).toFixed(1)}M`;
  }
  if (absoluteValue >= 1_000) {
    return `$${(absoluteValue / 1_000).toFixed(0)}K`;
  }
  return `$${absoluteValue.toLocaleString()}`;
}

/** Axis/tooltip currency with explicit negative sign (never abs-only). */
export function formatSignedCurrency(value: number): string {
  const rounded = Math.round(value);
  if (rounded === 0) return "$0";
  const sign = rounded < 0 ? "-" : "";
  return `${sign}${formatCompactCurrency(rounded)}`;
}

export const PROJECT_END_MARKER_COLOR = "#c084fc";
export const PROJECT_END_MARKER_LABEL = "Project End Date";

export interface ChartPeriodBounds {
  label: string;
  periodStart: Date;
  periodEnd: Date;
}

export function getDateProgressInVisibleRange(
  date: Date,
  rangeStart: Date,
  rangeEnd: Date,
): number | null {
  const duration = rangeEnd.getTime() - rangeStart.getTime();
  if (duration <= 0) return null;

  const progress = (date.getTime() - rangeStart.getTime()) / duration;
  if (progress < 0 || progress > 1) return null;
  return progress;
}

export function findChartPeriodLabelForDate(
  periods: ChartPeriodBounds[],
  targetDate: Date,
): string | null {
  const match = periods.find(
    (period) =>
      targetDate.getTime() >= period.periodStart.getTime() &&
      targetDate.getTime() <= period.periodEnd.getTime(),
  );
  return match?.label ?? null;
}

export function resolveProjectEndChartMarker(
  projectEndDate: Date | undefined,
  rangeStart: Date,
  rangeEnd: Date,
  periods: ChartPeriodBounds[],
): { periodLabel: string } | null {
  if (!projectEndDate) return null;
  if (
    getDateProgressInVisibleRange(projectEndDate, rangeStart, rangeEnd) === null
  ) {
    return null;
  }

  const periodLabel = findChartPeriodLabelForDate(periods, projectEndDate);
  return periodLabel ? { periodLabel } : null;
}

export type ReferenceLineLabelPosition = "top" | "bottom";

export interface ReferenceLineLabelPlacement {
  position: ReferenceLineLabelPosition;
  dy?: number;
}

/** Avoid overlapping top labels when zero-cash and project-end markers are nearby. */
export function resolveOverlappingReferenceLabelPlacements(
  zeroPeriodLabel: string | null | undefined,
  projectEndPeriodLabel: string | null | undefined,
  periodLabels: string[],
): {
  zero: ReferenceLineLabelPlacement;
  projectEnd: ReferenceLineLabelPlacement;
} {
  const defaultPlacement: ReferenceLineLabelPlacement = { position: "top" };

  if (!zeroPeriodLabel || !projectEndPeriodLabel) {
    return { zero: defaultPlacement, projectEnd: defaultPlacement };
  }

  const zeroIndex = periodLabels.indexOf(zeroPeriodLabel);
  const projectEndIndex = periodLabels.indexOf(projectEndPeriodLabel);

  if (zeroIndex === -1 || projectEndIndex === -1) {
    return { zero: defaultPlacement, projectEnd: defaultPlacement };
  }

  const indexDistance = Math.abs(zeroIndex - projectEndIndex);

  if (indexDistance === 0) {
    return {
      zero: { position: "top", dy: 0 },
      projectEnd: { position: "top", dy: 16 },
    };
  }

  if (indexDistance === 1) {
    return {
      zero: { position: "top" },
      projectEnd: { position: "bottom" },
    };
  }

  return { zero: defaultPlacement, projectEnd: defaultPlacement };
}

export function computeReferenceLabelChartMargins(
  zeroPlacement: ReferenceLineLabelPlacement | null,
  projectEndPlacement: ReferenceLineLabelPlacement | null,
): { top: number; bottom: number } {
  let top = 0;
  let bottom = 0;

  if (zeroPlacement?.position === "top") {
    top += 36;
  } else if (zeroPlacement?.position === "bottom") {
    bottom += 22;
  }

  if (projectEndPlacement?.position === "top") {
    top += projectEndPlacement.dy ? 18 : 24;
  } else if (projectEndPlacement?.position === "bottom") {
    bottom += 22;
  }

  if (
    zeroPlacement?.position === "top" &&
    projectEndPlacement?.position === "top" &&
    (projectEndPlacement.dy ?? 0) > 0
  ) {
    top = Math.max(top, 52);
  }

  return { top, bottom };
}

/** Timeline tick marks aligned to chart granularity. */
export function getTimelineSegmentMarkers(
  startDate: Date,
  endDate: Date,
): Array<{
  date: Date;
  type: "biweek" | "month" | "quarter";
  progress: number;
}> {
  const markers: Array<{
    date: Date;
    type: "biweek" | "month" | "quarter";
    progress: number;
  }> = [];
  const timeRange = endDate.getTime() - startDate.getTime();
  if (timeRange <= 0) return markers;

  const granularity = getChartGranularity(startDate, endDate);

  if (granularity === "biweekly") {
    let cursor = new Date(startDate);
    cursor.setUTCDate(cursor.getUTCDate() + BIWEEK_DAYS);
    while (cursor.getTime() < endDate.getTime()) {
      const progress =
        (cursor.getTime() - startDate.getTime()) / timeRange;
      if (progress > 0 && progress < 1) {
        markers.push({ date: new Date(cursor), type: "biweek", progress });
      }
      cursor.setUTCDate(cursor.getUTCDate() + BIWEEK_DAYS);
    }
    return markers;
  }

  const totalMonths =
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth());

  if (totalMonths <= 120) {
    const currentMonth = new Date(
      startDate.getFullYear(),
      startDate.getMonth() + 1,
      1,
    );
    while (currentMonth <= endDate) {
      const progress =
        (currentMonth.getTime() - startDate.getTime()) / timeRange;
      if (progress > 0 && progress < 1) {
        markers.push({ date: new Date(currentMonth), type: "month", progress });
      }
      currentMonth.setMonth(currentMonth.getMonth() + 1);
    }
  }

  const currentQuarter = new Date(
    startDate.getFullYear(),
    Math.floor(startDate.getMonth() / 3) * 3 + 3,
    1,
  );
  while (currentQuarter <= endDate) {
    const progress =
      (currentQuarter.getTime() - startDate.getTime()) / timeRange;
    if (progress > 0 && progress < 1) {
      markers.push({
        date: new Date(currentQuarter),
        type: "quarter",
        progress,
      });
    }
    currentQuarter.setMonth(currentQuarter.getMonth() + 3);
  }

  return markers;
}
